import { useState, useEffect } from "react";
import styles from "./ForgotPasswordForm.module.scss";
import classNames from "classnames/bind";
import { IForgotPasswordPayload } from "~/utils/interface/auth";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import { ArrowLeftIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "~/redux/hooks";
import { handleForgotPassword } from "~/thunks/auth/authThunk";
import { notify } from "~/utils/helper";
import { useTranslation } from "react-i18next";
import { adminRouteAbsolute } from "~/utils/constants/route";
const cx = classNames.bind(styles);

const ForgotPasswordForm = () => {
  const { t } = useTranslation(["admin/auth"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<IForgotPasswordPayload>({
    email: "",
  });
  const [errors, setErrors] = useState<{ email?: string }>({});
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Validate email
  const validateEmail = (email: string): string => {
    if (!email.trim()) {
      return t("email.required");
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t("email.invalid");
    }
    return "";
  };

  // Validate form
  const validateForm = (): boolean => {
    const emailError = validateEmail(formData.email);
    setErrors({ email: emailError });
    return !emailError;
  };

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touched) {
      if (name === "email") {
        const error = validateEmail(value);
        setErrors((prev) => ({ ...prev, email: error }));
      }
    }
  };

  const handleBackToLogin = () => {
    navigate(adminRouteAbsolute.login);
  };

  const handleSubmit = async () => {
    setTouched(true);

    try {
      if (validateForm()) {
        setLoading(true);
        await dispatch(handleForgotPassword(formData)).unwrap();
        localStorage.setItem("reset_password_email", formData.email.trim());
        notify("success", t("forgotPasswordForm.successMessage"));
        setCountdown(60);
      }
    } catch (e) {
      notify("error", (e as any).response?.data?.message || t("errorMessage"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  const getButtonTitle = (): string => {
    if (countdown > 0) {
      return `${t("resetPasswordButton")} (${countdown}s)`;
    }
    return t("resetPasswordButton");
  };

  const isButtonDisabled = countdown > 0 || loading;

  return (
    <div className={cx("forgot-password-form-container")}>
      <OutlinedButton
        title={t("backToLogin")}
        icon={<ArrowLeftIcon className={cx("back-to-login-icon")} />}
        onClick={handleBackToLogin}
        className={cx("back-to-login-button")}
      />
      <div className={cx("forgot-password-form-content")}>
        <div className={cx("forgot-password-label")}>
          <h3 className={cx("title")}>{t("forgotPasswordForm.title")}</h3>
          <p className={cx("description")}>
            {t("forgotPasswordForm.description")}
          </p>
        </div>

        <form className={cx("forgot-password-form")} onSubmit={handleFormSubmit}>
          <PrimaryInput
            name="email"
            label={t("email.label")}
            placeholder={t("email.placeholder")}
            value={formData.email}
            onChange={handleInputChange}
            errorMessage={errors.email}
          />

          <div className={cx("forgot-password-form-actions")}>
            <PrimaryButton
              title={getButtonTitle()}
              className={cx("reset-password-button")}
              type="submit"
              disabled={isButtonDisabled}
              loading={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
